// The indoor unit corner for customers, on /daikin-vrv-systems: how each
// Daikin VRV indoor unit looks in the room, its colours and its finish.
//
// 1. Choose the look (hidden, a panel in the ceiling, no false ceiling,
//    low on the floor, big spaces).  2. Pick a unit.  3. See it: a drawing
//    of the room in the colour you pick, the unit and Daikin's photo of it in
//    a room (both from Daikin's catalogue, and they follow the colour where
//    Daikin shows each panel), what you see, the finish, how it looks when it
//    is off and how it is cleaned. Every line carries its source.
//
// Units with a 3D model (src/data/unitModels.js) also open "See it in 3D"
// (Unit3D.jsx, loaded only when opened): turn it, switch it on and off, look
// through the ceiling and, on a phone, see it in your room.
//
// Ceiling depths stay with the architects (IndoorUnitCorner.jsx). Data:
// LOOKS and UNIT_LOOKS in src/data/indoorUnits.js; the pre-render writes the
// same text for Google (unitLooksHtml).

import { lazy, Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import RoomView from './RoomView';
import { track } from '../lib/track';
import { LOOKS, LOOK_IMG, LOOK_SOURCE, tonRange, unitsByLook } from '../data/indoorUnits';
import { MODELS_3D, has3D, teaser } from '../data/unitModels';

const Unit3D = lazy(() => import('./Unit3D'));

const GROUPS = unitsByLook();
const ALL = GROUPS.flatMap((g) => g.units);
const THUMB = { panel: '#ffffff', grille: '#ffffff', slot: '#64748b', base: '#ffffff' };
const lookTitle = (id) => (LOOKS.find((l) => l.id === id) || {}).title;

const CAPS = 'text-[11px] font-extrabold uppercase tracking-wider text-gray-500';

const CubeIcon = ({ className = 'h-4 w-4' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2l8 4.5v9L12 20l-8-4.5v-9z" /><path d="M12 11l8-4.5M12 11L4 6.5M12 11v9" /></svg>
);

function Icon({ kind }) {
  const common = { viewBox: '0 0 24 24', className: 'mt-0.5 h-4 w-4 shrink-0', fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': 'true' };
  if (kind === 'off') {
    return <svg {...common} stroke="#475569" strokeWidth="2.4"><path d="M18.4 6.6a9 9 0 1 1-12.8 0" /><path d="M12 2v10" /></svg>;
  }
  if (kind === 'clean') {
    return <svg {...common} stroke="#15803d" strokeWidth="2.4"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6" /></svg>;
  }
  return <svg {...common} stroke="#2563eb" strokeWidth="3"><path d="M20 6L9 17l-5-5" /></svg>;
}

function Source({ s }) {
  return s === 'daikin'
    ? <span className="mt-0.5 shrink-0 self-start rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold tracking-wide text-blue-800">DAIKIN</span>
    : <span className="mt-0.5 shrink-0 self-start rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-bold tracking-wide text-green-800">CITI COOL</span>;
}

function Bullets({ title, items, icon }) {
  if (!items || !items.length) return null;
  return (
    <div className="mt-5">
      <p className={CAPS}>{title}</p>
      <ul className="mt-2 flex flex-col gap-2">
        {items.map(([t, s]) => (
          <li key={t} className="flex items-start gap-2 text-[15px] leading-relaxed text-gray-800">
            <Icon kind={icon} />
            <span className="min-w-0 flex-1">{t}</span>
            <Source s={s} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Swatches({ u, ci, onPick }) {
  if (u.colours) {
    return (
      <div className="mt-4">
        <p className={CAPS}>{u.colourLabel}: <span className="text-gray-900">{u.colours[ci].name}</span></p>
        <div className="mt-2 flex flex-wrap gap-2">
          {u.colours.map((k, i) => {
            const on = i === ci;
            return (
              <button key={k.name} type="button" onClick={() => onPick(i)} aria-pressed={on}
                className={`flex h-10 items-center gap-2 rounded-full pl-2 pr-3.5 text-sm ${on ? 'border-2 border-blue-600 bg-blue-50 font-bold text-blue-900' : 'border border-gray-300 bg-white font-semibold text-gray-700 hover:border-gray-400'}`}>
                <span className="h-6 w-6 shrink-0 rounded-full" style={{ background: k.sw, border: `${k.edgeW}px solid ${k.edge}`, boxSizing: 'border-box' }} />
                {k.name}
              </button>
            );
          })}
        </div>
        <p className="mt-1.5 text-xs text-gray-500">{u.colourNote}</p>
      </div>
    );
  }
  if (u.finishNote) {
    return (
      <div className="mt-4 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3">
        <p className={CAPS}>Finish</p>
        <p className="mt-1 text-[15px] font-bold text-gray-900">{u.finishNote}</p>
      </div>
    );
  }
  return null;
}

function DaikinImages({ u, colour, onVisit }) {
  const img = (colour && colour.img) || u.img;
  const photo = (colour && colour.photo) || u.photo;
  const named = colour && colour.img && u.colours.length > 1;
  return (
    <div className="mt-5">
      <p className={CAPS}>From Daikin’s catalogue</p>
      <div className={`mt-2 grid gap-3 ${photo ? 'grid-cols-2' : 'grid-cols-1 sm:max-w-sm'}`}>
        <figure>
          <div className="flex aspect-[4/3] items-center justify-center rounded-xl border border-gray-200 bg-white p-2">
            <img src={LOOK_IMG(img)} alt={`Daikin ${u.series} ${u.name}${named ? `, ${colour.name} panel` : ''}`} loading="lazy" className="max-h-full max-w-full object-contain" />
          </div>
          <figcaption className="mt-1 text-xs leading-snug text-gray-500">The unit{named ? `: ${colour.name} panel` : ''}</figcaption>
        </figure>
        {photo && (
          <figure>
            <div className="aspect-[4/3] overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
              <img src={LOOK_IMG(photo.src)} alt={photo.cap} loading="lazy" className="h-full w-full object-cover" />
            </div>
            <figcaption className="mt-1 text-xs leading-snug text-gray-500">{photo.cap}</figcaption>
          </figure>
        )}
      </div>
      <p className="mt-2 text-xs leading-relaxed text-gray-500">
        Images: Daikin VRV Alpha catalogue.{' '}
        <button type="button" onClick={onVisit} className="font-bold text-blue-700 hover:underline">See the real finish at our Experience Center ›</button>
      </p>
    </div>
  );
}

function Detail({ u, ci, onColour, onVisit, on3D }) {
  const colour = u.colours ? u.colours[ci] : null;
  const three = has3D(u.id);
  return (
    <div className="mt-5 rounded-2xl border border-gray-100 bg-white p-4 shadow-xl sm:p-6 lg:p-7">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-xl font-bold text-gray-900 lg:text-[28px]">{u.name}</h3>
        <span className="text-sm text-gray-500 lg:text-[15px]">{tonRange(u)} · {lookTitle(u.look)}</span>
      </div>

      <div className="mt-4 grid items-start gap-6 lg:mt-5 lg:grid-cols-[minmax(0,1.12fr)_minmax(0,1fr)] lg:gap-8">
        <div>
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
            <RoomView kind={u.room} colour={colour} label={`Drawing of a room with the ${u.name}: ${u.see}`} />
            {three && (
              <button type="button" onClick={on3D}
                className="absolute bottom-3 right-3 inline-flex h-10 items-center gap-1.5 rounded-full bg-gray-900/90 px-4 text-sm font-bold text-white shadow-lg hover:bg-gray-900">
                <CubeIcon />See it in 3D
              </button>
            )}
          </div>
          <p className="mt-1.5 text-xs text-gray-400">Our drawing, to show the look. Not to scale.</p>
          {three && (
            <button type="button" onClick={on3D}
              className="mt-3 flex w-full items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-left hover:border-blue-300">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-blue-600 text-white"><CubeIcon className="h-5 w-5" /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-extrabold text-blue-900">See it in 3D</span>
                <span className="block text-[13px] leading-snug text-blue-800">{teaser(MODELS_3D[u.id])}</span>
              </span>
              <span className="text-lg font-bold text-blue-700" aria-hidden="true">›</span>
            </button>
          )}
          <Swatches u={u} ci={ci} onPick={onColour} />
          <DaikinImages u={u} colour={colour} onVisit={onVisit} />
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-blue-700">Daikin {u.series}</span>
            <span className="rounded-full bg-gray-900 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white">{u.tag}</span>
          </div>
          <div className="mt-3.5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <p className={CAPS}>What you see in the room</p>
            <p className="mt-1.5 text-base font-bold leading-snug text-gray-900 lg:text-[17px]">{u.see}</p>
          </div>
          {u.quote && (
            <blockquote className="mt-3.5 border-l-[3px] border-blue-600 py-1 pl-3.5">
              <p className="text-[15px] italic leading-relaxed text-gray-800 lg:text-base">“{u.quote}”</p>
              <p className="mt-1 text-xs text-gray-500">Daikin, VRV Alpha catalogue</p>
            </blockquote>
          )}
          <Bullets title="Finish and look" items={u.finish} icon="check" />
          <Bullets title="When it is switched off" items={u.off} icon="off" />
          <Bullets title="Keeping it clean" items={u.clean} icon="clean" />
          <div className="mt-5 grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-gray-50 p-3">
              <p className={CAPS}>Capacity</p>
              <p className="mt-1 text-lg font-extrabold text-gray-900">{tonRange(u)}</p>
            </div>
            <div className="rounded-xl bg-gray-50 p-3">
              <p className={CAPS}>False ceiling</p>
              <p className="mt-1 text-lg font-extrabold text-gray-900">{u.falseCeiling}</p>
            </div>
          </div>
          <div className="mt-3.5 flex flex-wrap items-center gap-1.5">
            <span className="text-[13px] font-bold text-gray-700">Best for</span>
            {u.best.map((b) => <span key={b} className="rounded-full bg-blue-50 px-2.5 py-1 text-[13px] font-semibold text-blue-900">{b}</span>)}
          </div>
          <p className="mt-4 text-sm text-gray-600">
            Ceiling depth, pipes and drains for this unit:{' '}
            <Link to="/architects-interior-designers#units" className="font-bold text-blue-700 hover:underline">Architect Corner ›</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function IndoorUnitLooks({ initialLook = 'panel', initialUnit = 'round-flow-cassette', onVisit }) {
  const [look, setLook] = useState(initialLook);
  const [sel, setSel] = useState(initialUnit);
  const [ci, setCi] = useState(0);
  const [open3D, setOpen3D] = useState(false);

  const group = GROUPS.find((g) => g.id === look) || GROUPS[0];
  const u = ALL.find((x) => x.id === sel) || group.units[0];
  const colourIndex = u.colours ? Math.min(ci, u.colours.length - 1) : 0;

  const pickLook = (id) => {
    const first = GROUPS.find((g) => g.id === id).units[0];
    setLook(id); setSel(first.id); setCi(0);
    track('indoor_unit_look', { look: id, unit: first.id, page: 'vrv' });
  };
  const pickUnit = (id) => {
    setSel(id); setCi(0);
    track('indoor_unit_look', { look, unit: id, page: 'vrv' });
  };
  const pickColour = (i) => {
    setCi(i);
    track('indoor_unit_colour', { unit: u.id, colour: u.colours[i].name, page: 'vrv' });
  };

  return (
    <div>
      <p className="mb-3 text-[13px] font-extrabold uppercase tracking-wider text-blue-600">1 · Choose the look</p>
      <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6 lg:mx-0 lg:overflow-visible lg:px-0 lg:pb-0">
        <div className="flex w-max gap-2.5 lg:grid lg:w-auto lg:grid-cols-5 lg:gap-3.5" role="group" aria-label="Choose the look">
          {GROUPS.map((g) => {
            const on = g.id === look;
            return (
              <button key={g.id} type="button" onClick={() => pickLook(g.id)} aria-pressed={on}
                className={`block w-[156px] shrink-0 rounded-2xl p-2.5 text-left transition-colors lg:w-auto ${on ? 'border-2 border-blue-600 bg-blue-50 shadow-lg shadow-blue-600/20' : 'border border-gray-200 bg-white hover:border-blue-300'}`}>
                <span className="block overflow-hidden rounded-xl border border-gray-200">
                  <RoomView kind={g.room} colour={THUMB} className="[&_text]:hidden" />
                </span>
                <span className={`mt-2.5 block text-[15px] font-extrabold ${on ? 'text-blue-900' : 'text-gray-900'}`}>{g.title}</span>
                <span className={`mt-0.5 block text-[13px] leading-snug ${on ? 'text-blue-800' : 'text-gray-500'}`}>{g.sub}</span>
                <span className={`mt-1.5 block text-xs font-bold ${on ? 'text-blue-800' : 'text-gray-500'}`}>{g.units.length} {g.units.length === 1 ? 'unit' : 'units'}</span>
              </button>
            );
          })}
        </div>
      </div>

      <p className="mb-3 mt-6 text-[13px] font-extrabold uppercase tracking-wider text-blue-600 lg:mt-7">2 · Pick a unit</p>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Pick a unit">
        {group.units.map((x) => {
          const on = x.id === u.id;
          return (
            <button key={x.id} type="button" onClick={() => pickUnit(x.id)} aria-pressed={on}
              className={`flex flex-col items-start rounded-xl px-3.5 py-2 text-left ${on ? 'border-2 border-blue-600 bg-blue-600' : 'border border-gray-300 bg-white hover:border-gray-400'}`}>
              <span className={`flex items-center gap-1.5 text-sm ${on ? 'font-extrabold text-white' : 'font-bold text-gray-900'}`}>
                {x.name}
                {has3D(x.id) && <span className={`rounded px-1 text-[10px] font-extrabold ${on ? 'bg-white text-blue-700' : 'bg-blue-600 text-white'}`}>3D</span>}
              </span>
              <span className={`text-xs ${on ? 'text-blue-100' : 'text-gray-500'}`}>{x.series} · {tonRange(x)}</span>
            </button>
          );
        })}
      </div>

      <Detail u={u} ci={colourIndex} onColour={pickColour} onVisit={onVisit}
        on3D={() => { setOpen3D(true); track('unit_3d_open', { unit: u.id, page: 'vrv' }); }} />

      {open3D && has3D(u.id) && (
        <Suspense fallback={(
          <div className="fixed inset-0 z-[70] grid place-items-center bg-gray-900/60">
            <p className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow">Loading 3D…</p>
          </div>
        )}>
          <Unit3D u={u} ci={colourIndex} onColour={pickColour} onClose={() => setOpen3D(false)} />
        </Suspense>
      )}

      <p className="mx-auto mt-4 max-w-3xl text-center text-xs leading-relaxed text-gray-500">{LOOK_SOURCE}</p>
    </div>
  );
}
